import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'baseui/modal';
import { Notification, KIND } from 'baseui/notification';
import { CreateRoomData, ERoomType } from './types';
import { roomApi } from './api';

interface CreateRoomModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: (roomId: string) => void;
}

const LabeledInput: React.FC<{
	label: string;
	type?: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
}> = ({ label, type = 'text', value, onChange, placeholder }) => {
	const [css] = useStyletron();
	return (
		<div className={css({ marginBottom: '0' })}>
			<label
				className={css({
					display: 'block',
					fontSize: '0.9rem',
					fontWeight: '600',
					color: '#374151',
					marginBottom: '8px'
				})}>
				{label}
			</label>
			<Input
				type={type}
				placeholder={placeholder || label}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				overrides={{
					Root: {
						style: {
							marginBottom: '0',
							width: '100%'
						}
					}
				}}
			/>
		</div>
	);
};

const LabeledMultiSelect: React.FC<{
	label: string;
	options: Array<{ label: string; value: string }>;
	value: string[];
	onChange: (value: string[]) => void;
	placeholder?: string;
}> = ({ label, options, value, onChange, placeholder }) => {
	const [css] = useStyletron();
	const [isOpen, setIsOpen] = useState(false);

	const selectedLabels = options.filter((opt) => value.includes(opt.value)).map((opt) => opt.label);
	const displayText = selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder || `Select ${label}`;

	const toggleOption = (optValue: string) => {
		if (value.includes(optValue)) {
			onChange(value.filter((v) => v !== optValue));
		} else {
			onChange([...value, optValue]);
		}
	};

	return (
		<div className={css({ marginBottom: '0', position: 'relative' })}>
			<label
				className={css({
					display: 'block',
					fontSize: '0.9rem',
					fontWeight: '600',
					color: '#374151',
					marginBottom: '8px'
				})}>
				{label}
			</label>
			<div
				className={css({
					position: 'relative',
					width: '100%'
				})}>
				<button
					type='button'
					onClick={() => setIsOpen(!isOpen)}
					className={css({
						width: '100%',
						padding: '12px 16px',
						border: '1px solid #d1d5db',
						borderRadius: '8px',
						backgroundColor: '#ffffff',
						textAlign: 'left',
						fontSize: '0.9rem',
						color: selectedLabels.length > 0 ? '#374151' : '#9ca3af',
						cursor: 'pointer',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						':hover': {
							borderColor: '#9ca3af'
						},
						':focus': {
							outline: 'none',
							borderColor: '#3b82f6',
							boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
						}
					})}>
					<span className={css({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>{displayText}</span>
					<span
						className={css({
							transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
							transition: 'transform 0.2s ease',
							fontSize: '0.8rem',
							color: '#6b7280'
						})}>
						▼
					</span>
				</button>

				{isOpen && (
					<div
						className={css({
							position: 'absolute',
							top: '100%',
							left: 0,
							right: 0,
							backgroundColor: '#ffffff',
							border: '1px solid #d1d5db',
							borderRadius: '8px',
							boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
							zIndex: 1000,
							marginTop: '4px'
						})}>
						{options.map((option) => (
							<button
								key={option.value}
								type='button'
								onClick={() => toggleOption(option.value)}
								className={css({
									width: '100%',
									padding: '12px 16px',
									border: 'none',
									backgroundColor: value.includes(option.value) ? '#e0f2fe' : '#ffffff',
									textAlign: 'left',
									fontSize: '0.9rem',
									color: '#374151',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									':hover': {
										backgroundColor: value.includes(option.value) ? '#bae6fd' : '#f9fafb'
									},
									':first-child': {
										borderTopLeftRadius: '8px',
										borderTopRightRadius: '8px'
									},
									':last-child': {
										borderBottomLeftRadius: '8px',
										borderBottomRightRadius: '8px'
									}
								})}>
								<span
									className={css({
										width: '16px',
										height: '16px',
										border: value.includes(option.value) ? '2px solid #3b82f6' : '2px solid #d1d5db',
										borderRadius: '4px',
										backgroundColor: value.includes(option.value) ? '#3b82f6' : 'transparent',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										color: '#ffffff',
										fontSize: '0.7rem'
									})}>
									{value.includes(option.value) && '✓'}
								</span>
								{option.label}
							</button>
						))}
					</div>
				)}
			</div>

			{/* Overlay to close dropdown when clicking outside */}
			{isOpen && (
				<button
					type='button'
					className={css({
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						zIndex: 999,
						border: 'none',
						background: 'transparent',
						cursor: 'default'
					})}
					onClick={() => setIsOpen(false)}
					aria-label='Close dropdown'
				/>
			)}
		</div>
	);
};

const LabeledSelect: React.FC<{
	label: string;
	options: Array<{ label: string; value: string }>;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
}> = ({ label, options, value, onChange, placeholder }) => {
	const [css] = useStyletron();
	const [isOpen, setIsOpen] = useState(false);

	const selectedOption = options.find((option) => option.value === value);
	const displayText = selectedOption ? selectedOption.label : placeholder || `Select ${label}`;
	return (
		<div className={css({ marginBottom: '0', position: 'relative' })}>
			<label
				className={css({
					display: 'block',
					fontSize: '0.9rem',
					fontWeight: '600',
					color: '#374151',
					marginBottom: '8px'
				})}>
				{label}
			</label>
			<div
				className={css({
					position: 'relative',
					width: '100%'
				})}>
				<button
					type='button'
					onClick={() => setIsOpen(!isOpen)}
					className={css({
						width: '100%',
						padding: '12px 16px',
						border: '1px solid #d1d5db',
						borderRadius: '8px',
						backgroundColor: '#ffffff',
						textAlign: 'left',
						fontSize: '0.9rem',
						color: selectedOption ? '#374151' : '#9ca3af',
						cursor: 'pointer',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						':hover': {
							borderColor: '#9ca3af'
						},
						':focus': {
							outline: 'none',
							borderColor: '#3b82f6',
							boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
						}
					})}>
					<span>{displayText}</span>
					<span
						className={css({
							transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
							transition: 'transform 0.2s ease',
							fontSize: '0.8rem',
							color: '#6b7280'
						})}>
						▼
					</span>
				</button>

				{isOpen && (
					<div
						className={css({
							position: 'absolute',
							top: '100%',
							left: 0,
							right: 0,
							backgroundColor: '#ffffff',
							border: '1px solid #d1d5db',
							borderRadius: '8px',
							boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
							zIndex: 1000,
							marginTop: '4px'
						})}>
						{options.map((option) => (
							<button
								key={option.value}
								type='button'
								onClick={() => {
									onChange(option.value);
									setIsOpen(false);
								}}
								className={css({
									width: '100%',
									padding: '12px 16px',
									border: 'none',
									backgroundColor: value === option.value ? '#f3f4f6' : '#ffffff',
									textAlign: 'left',
									fontSize: '0.9rem',
									color: '#374151',
									cursor: 'pointer',
									':hover': {
										backgroundColor: '#f9fafb'
									},
									':first-child': {
										borderTopLeftRadius: '8px',
										borderTopRightRadius: '8px'
									},
									':last-child': {
										borderBottomLeftRadius: '8px',
										borderBottomRightRadius: '8px'
									}
								})}>
								{option.label}
							</button>
						))}
					</div>
				)}
			</div>

			{/* Overlay to close dropdown when clicking outside */}
			{isOpen && (
				<button
					type='button'
					className={css({
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						zIndex: 999,
						border: 'none',
						background: 'transparent',
						cursor: 'default'
					})}
					onClick={() => setIsOpen(false)}
					aria-label='Close dropdown'
				/>
			)}
		</div>
	);
};

const CreateRoomModal: React.FC<CreateRoomModalProps> = ({ isOpen, onClose, onSuccess }) => {
	const [css] = useStyletron();
	const [loading, setLoading] = useState(false);
	const [notification, setNotification] = useState<{ message: string; kind: typeof KIND[keyof typeof KIND] } | null>(null);
	const [createRoomData, setCreateRoomData] = useState<CreateRoomData>({
		hostId: '',
		referralCode: '',
		hostName: '',
		title: '',
		tags: '',
		thumbnail: '',
		priorityOrder: '',
		badgeIcon: '',
		badgeText: '',
		membersAllowed: '',
		speakersAllowed: '',
		initialEntryFee: '',
		timeAllowedForSpeaker: '',
		currencyType: '',
		segmentNames: '',
		languages: [],
		charges: '',
		durationInMinutes: '',
		roomType: ''
	});

	const currencyOptions = [
		{ label: 'INR', value: 'INR' },
		{ label: 'COINS', value: 'COINS' },
		{ label: 'CASH', value: 'CASH' }
	];

	const languageOptions = [
		{ label: 'Hindi', value: 'Hindi' },
		{ label: 'English', value: 'English' }
	];

	const roomTypeOptions = [
		{ label: 'Group Chat', value: ERoomType.GROUP_CHAT },
		{ label: 'One on One', value: ERoomType.ONE_ON_ONE }
	];

	const handleCreateRoom = async () => {
		if (!createRoomData.hostId.trim() && !createRoomData.referralCode.trim()) {
			setNotification({ message: 'Either Host ID or Referral Code is required', kind: KIND.negative });
			return;
		}
		setLoading(true);
		setNotification(null);
		try {
			const response = await roomApi.createRoom(createRoomData);
			if (response.success) {
				setNotification({
					message: `Room created successfully! Room ID: ${response.data.roomId}`,
					kind: KIND.positive
				});
				// Reset form
				setCreateRoomData({
					hostId: '',
					referralCode: '',
					hostName: '',
					title: '',
					tags: '',
					thumbnail: '',
					priorityOrder: '',
					badgeIcon: '',
					badgeText: '',
					membersAllowed: '',
					speakersAllowed: '',
					initialEntryFee: '',
					timeAllowedForSpeaker: '',
					currencyType: '',
					segmentNames: '',
					languages: [],
					charges: '',
					durationInMinutes: '',
					roomType: ''
				});
				// Call success callback with room ID
				onSuccess(response.data.roomId);
				// Close modal after a short delay to show success message
				setTimeout(() => {
					onClose();
				}, 2000);
			} else {
				setNotification({ message: response.error || 'Failed to create room', kind: KIND.negative });
			}
		} catch (error: any) {
			setNotification({ message: 'Error creating room', kind: KIND.negative });
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setNotification(null);
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size='large'
			overrides={{
				Dialog: {
					style: {
						width: '90vw',
						maxWidth: '1200px',
						minWidth: '800px'
					}
				}
			}}>
			<ModalHeader>Create New Room</ModalHeader>
			<ModalBody>
				{/* Notifications */}
				{notification && (
					<div className={css({ marginBottom: '24px' })}>
						<Notification kind={notification.kind} onClose={() => setNotification(null)}>
							{notification.message}
						</Notification>
					</div>
				)}

				<div
					className={css({
						display: 'grid',
						gridTemplateColumns: 'repeat(3, 1fr)',
						gap: '20px',
						'@media (max-width: 1200px)': {
							gridTemplateColumns: 'repeat(2, 1fr)'
						},
						'@media (max-width: 768px)': {
							gridTemplateColumns: '1fr'
						}
					})}>
					<LabeledInput
						label='Host ID'
						type='text'
						value={createRoomData.hostId}
						onChange={(value) => setCreateRoomData({ ...createRoomData, hostId: value })}
						placeholder='Enter host ID (e.g., 176423016005160)'
					/>
					<LabeledInput
						label='Referral Code'
						type='text'
						value={createRoomData.referralCode}
						onChange={(value) => setCreateRoomData({ ...createRoomData, referralCode: value })}
						placeholder='Enter referral code (alternative to Host ID)'
					/>
					<LabeledInput
						label='Host Name'
						value={createRoomData.hostName}
						onChange={(value) => setCreateRoomData({ ...createRoomData, hostName: value })}
					/>
					<LabeledInput
						label='Room Title'
						value={createRoomData.title}
						onChange={(value) => setCreateRoomData({ ...createRoomData, title: value })}
					/>
					<LabeledInput
						label='Tags (comma separated)'
						value={createRoomData.tags}
						onChange={(value) => setCreateRoomData({ ...createRoomData, tags: value })}
					/>
					<LabeledInput
						label='Thumbnail URL'
						value={createRoomData.thumbnail}
						onChange={(value) => setCreateRoomData({ ...createRoomData, thumbnail: value })}
					/>
					<LabeledInput
						label='Priority Order'
						type='number'
						value={createRoomData.priorityOrder}
						onChange={(value) => setCreateRoomData({ ...createRoomData, priorityOrder: value })}
					/>
					<LabeledInput
						label='Badge Icon'
						value={createRoomData.badgeIcon}
						onChange={(value) => setCreateRoomData({ ...createRoomData, badgeIcon: value })}
					/>
					<LabeledInput
						label='Badge Text'
						value={createRoomData.badgeText}
						onChange={(value) => setCreateRoomData({ ...createRoomData, badgeText: value })}
					/>
					<LabeledInput
						label='Members Allowed'
						type='number'
						value={createRoomData.membersAllowed}
						onChange={(value) => setCreateRoomData({ ...createRoomData, membersAllowed: value })}
					/>
					<LabeledInput
						label='Speakers Allowed'
						type='number'
						value={createRoomData.speakersAllowed}
						onChange={(value) => setCreateRoomData({ ...createRoomData, speakersAllowed: value })}
					/>
					<LabeledInput
						label='Initial Entry Fee'
						type='number'
						value={createRoomData.initialEntryFee}
						onChange={(value) => setCreateRoomData({ ...createRoomData, initialEntryFee: value })}
					/>
					<LabeledInput
						label='Time Allowed for Speaker (seconds)'
						type='number'
						value={createRoomData.timeAllowedForSpeaker}
						onChange={(value) => setCreateRoomData({ ...createRoomData, timeAllowedForSpeaker: value })}
					/>
					<LabeledSelect
						label='Currency Type'
						options={currencyOptions}
						value={createRoomData.currencyType}
						onChange={(value) => setCreateRoomData({ ...createRoomData, currencyType: value })}
						placeholder='Select Currency Type'
					/>
					<LabeledInput
						label='Segment Names (comma separated)'
						value={createRoomData.segmentNames}
						onChange={(value) => setCreateRoomData({ ...createRoomData, segmentNames: value })}
						placeholder='e.g., VIP, Premium, Standard'
					/>
					<LabeledMultiSelect
						label='Languages'
						options={languageOptions}
						value={createRoomData.languages}
						onChange={(value) => setCreateRoomData({ ...createRoomData, languages: value })}
						placeholder='Select Languages'
					/>
					<LabeledInput
						label='Charges'
						type='number'
						value={createRoomData.charges}
						onChange={(value) => setCreateRoomData({ ...createRoomData, charges: value })}
						placeholder='Enter charges amount'
					/>
					<LabeledInput
						label='Duration (Minutes)'
						type='number'
						value={createRoomData.durationInMinutes}
						onChange={(value) => setCreateRoomData({ ...createRoomData, durationInMinutes: value })}
						placeholder='Enter duration in minutes'
					/>
					<LabeledSelect
						label='Room Type'
						options={roomTypeOptions}
						value={createRoomData.roomType}
						onChange={(value) => setCreateRoomData({ ...createRoomData, roomType: value })}
						placeholder='Select Room Type'
					/>
				</div>
			</ModalBody>
			<ModalFooter>
				<div className={css({ display: 'flex', gap: '12px', width: '100%' })}>
					<Button kind='tertiary' onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button onClick={handleCreateRoom} isLoading={loading} disabled={loading}>
						Create Room
					</Button>
				</div>
			</ModalFooter>
		</Modal>
	);
};

export default CreateRoomModal;
